import { FC, useEffect } from 'react';

import styled from '@emotion/styled';
import { ServerErrors, BcCheckbox } from 'components';
import DOMPurify from 'dompurify';

import { Button } from '@britishcouncil/react-solas';
import {
  DateField,
  LinkButton,
  NewPasswordField,
  Quote,
  TextField,
  TnCCheckbox,
} from '@britishcouncil/react-solas-ors3';
import { FormFields, ServerError } from '@britishcouncil/react-forms';

import { SignupFormModel } from '../types';

export const SignupForm: FC<{
  fields: FormFields<SignupFormModel>;
  showPasswordForm: boolean;
  showMinorForm: boolean;
  showEmailForm: boolean;
  showSubmitBtn: boolean;
  isSubmitting: boolean;
  mainErrors: ServerError[];
  tnCText: string;
  onSubmit: () => void;
  onTnCClick: () => void;
}> = ({
  fields,
  showMinorForm,
  showPasswordForm,
  showSubmitBtn,
  isSubmitting,
  mainErrors,
  showEmailForm,
  tnCText,
  onSubmit,
  onTnCClick,
}) => {

    useEffect(() => {
      if (!fields.hasFamilyName.value) {
        fields.lastName.onChange(null);
      } else if (!fields.lastName.value) {
        fields.lastName.onChange("");
      }
    }, [fields.hasFamilyName.value, fields.lastName, fields.lastName.value]);

    return (
      <form>
        <TextField label="First name *" {...fields.firstName} showColon={false} />
        <TextField label="Last name" {...fields.lastName} showColon={false} disabled={!fields.hasFamilyName.value} value={fields.lastName.value ?? ''} />
        <S.CheckBoxContainer>
          <BcCheckbox {
            ...fields.hasFamilyName}
            value={!fields.hasFamilyName.value}
            onChange={(val) => fields.hasFamilyName.onChange(!val)}>
            I do not have a last name
          </BcCheckbox>
        </S.CheckBoxContainer>

        <DateField label="Date of birth *" mode="dob" {...fields.dateOfBirth} showColon={false} />
        {showEmailForm && (
          <>
            <TextField label="Email *" {...fields.email} showColon={false} />
            <TextField label="Confirm email *" {...fields.confirmEmail} showColon={false} />
          </>
        )}
        {showPasswordForm && <NewPasswordField label="Password *" {...fields.password} showColon={false} />}
        {showMinorForm && (
          <>
            <Quote title="Warning!" intent="warning">
              You need to be over 18 years old to book a test
            </Quote>
            <p>Please provide the email address for a parent or guardian that can register and book a test for you:</p>
            <TextField label="Their email *" {...fields.parentEmail} showColon={false} />
            <TextField label="Their relationship to you *" {...fields.relationshipToChild} showColon={false} />
          </>
        )}
        {showSubmitBtn && (
          <>
            <div>
              <h3>Please read the Account Registration Terms and Conditions</h3>
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tnCText) }}></span>
            </div>
            <TnCCheckbox {...fields.tnc}>
              I have read and accepted the Data Privacy Notice and Account Registration Terms and Conditions{' '}
              <LinkButton onClick={onTnCClick}>Exams Data Privacy Notice and Terms & Conditions</LinkButton>
            </TnCCheckbox>

            {!isSubmitting && <ServerErrors errors={mainErrors} />}

            <CompleteBtn intent="primary" onClick={onSubmit} type="button" disabled={isSubmitting} loading={isSubmitting}>
              Complete account registration
            </CompleteBtn>
          </>
        )}
      </form>
    )
  };

const CompleteBtn = styled(Button)`
  display: block;
`;

const S = {
  CheckBoxContainer: styled.div`
    .checkbox {
      label {
        padding: 0 !important;

        input {
          position: relative;
          top: 0;
          left: 0;
          margin-right: 32px;
          margin-left: 12px;
        }
      }
    }
  `
};

